import {
  DEFAULT_COLOR,
  DEFAULT_WIDTH,
  MAX_OPLOG_BYTES,
  OPLOG_WARN_RATIO,
  opLogBytes,
  roundCoord,
  roundPoint,
  type Op,
  type OpLog,
  type Point,
  type StrokeOp,
  type StrokeSnap,
} from '@panels/shared';

/**
 * The drawing session: the op log being built, plus undo/redo (§7).
 *
 * Pure and DOM-free so the fiddly parts — press-and-hold partial undo, and redo
 * replaying a truncated tail — can be tested directly.
 *
 * GUARDRAIL #3: everything here operates on the raw captured points, kept at
 * storage precision (4 decimals) from the moment they are captured, so what is on
 * screen is exactly what gets stored. Smoothing is strictly a render-time concern.
 */

export type RedoEntry =
  /** A whole op was removed (tap undo, or a hold that rewound a stroke to nothing). */
  | { type: 'op'; op: Op }
  /** A stroke was rewound part-way; these are the points that came off the end. */
  | { type: 'tail'; opIndex: number; points: Point[] }
  /** A straightened stroke went back to hand-drawn; `points` is the aiming that followed the snap. */
  | { type: 'snap'; opIndex: number; snap: StrokeSnap; points: Point[] };

/** What the next stroke is drawn with. The eraser is just a white pen (see ops.ts). */
export interface Pen {
  color: string;
  width: number;
}

export interface DrawingSnapshot {
  ops: Op[];
  redo: RedoEntry[];
}

export interface DrawingSessionOptions {
  color?: string;
  width?: number;
  now?: () => number;
  /** Serialized size limit. Always MAX_OPLOG_BYTES in the app; smaller in tests. */
  maxBytes?: number;
}

/**
 * Room kept free while a stroke grows, so straightening it (which adds a `snap`
 * field) can never push the log over the limit.
 */
const SNAP_RESERVE_BYTES = 40;

/** Enough for any new stroke's first point, style and a snap: below this much room, the panel is full. */
const NEW_STROKE_BYTES = 128;

export type SizeLevel = 'ok' | 'warn' | 'full';

export class DrawingSession {
  ops: Op[] = [];
  redo: RedoEntry[] = [];

  private color: string;
  private strokeWidth: number;
  private now: () => number;

  private strokeStartedAt = 0;
  private drawing = false;

  /** Points removed so far by the in-flight press-and-hold rewind, oldest first. */
  private pendingTail: Point[] | null = null;
  private pendingOpIndex = -1;
  private pendingOriginal: Op | null = null;
  private pendingRemovedOp = false;

  /**
   * Serialized size of the op log, exactly as the server will measure it (Round
   * 2.2 §3). Recounted after every structural change and kept up to date point
   * by point while a stroke grows. It can lag only during a hold-rewind, where it
   * overestimates until release.
   */
  bytes: number;
  readonly maxBytes: number;

  constructor(options: DrawingSessionOptions = {}) {
    this.color = options.color ?? DEFAULT_COLOR;
    this.strokeWidth = options.width ?? DEFAULT_WIDTH;
    this.now = options.now ?? (() => performance.now());
    this.maxBytes = options.maxBytes ?? MAX_OPLOG_BYTES;
    this.bytes = opLogBytes(this.toOpLog());
  }

  private measure(): void {
    this.bytes = opLogBytes(this.toOpLog());
  }

  /** No room for another stroke. Undo and Done still work. */
  get isFull(): boolean {
    return this.bytes + NEW_STROKE_BYTES > this.maxBytes;
  }

  /** For the drawing screen: a gentle note at 75% of the limit, a hard stop at it. */
  get sizeLevel(): SizeLevel {
    if (this.isFull) return 'full';
    return this.bytes >= this.maxBytes * OPLOG_WARN_RATIO ? 'warn' : 'ok';
  }

  get isDrawing(): boolean {
    return this.drawing;
  }

  get canUndo(): boolean {
    return this.ops.length > 0;
  }

  get canRedo(): boolean {
    return this.redo.length > 0;
  }

  /** Switch pen or eraser. Affects strokes from now on; finished ones keep their own style. */
  setPen(pen: Pen): void {
    this.color = pen.color;
    this.strokeWidth = pen.width;
  }

  get pen(): Pen {
    return { color: this.color, width: this.strokeWidth };
  }

  /* -------------------------------------------------------------- input */

  /**
   * `straight` starts a Shift stroke: snapped from its very first point.
   * Returns false, and changes nothing, when the panel is full.
   */
  beginStroke(x: number, y: number, pressure?: number, options: { straight?: boolean } = {}): boolean {
    // Commit any in-flight rewind first, so the size check sees the real log.
    this.cancelPartialUndo();
    // Read the clock once, so the first point is exactly t=0 by construction.
    const startedAt = this.now();

    const stroke: StrokeOp = {
      kind: 'stroke',
      // GUARDRAIL #2: style travels with the op, even though the MVP pen never changes.
      color: this.color,
      width: this.strokeWidth,
      points: [this.pointAt(x, y, pressure, 0)],
    };
    if (options.straight) stroke.snap = { kind: 'line', at: 0 };

    const added = JSON.stringify(stroke).length + (this.ops.length > 0 ? 1 : 0);
    if (this.bytes + added + SNAP_RESERVE_BYTES > this.maxBytes) return false;

    // New input always wins over anything that was waiting to be redone.
    this.redo = [];
    this.drawing = true;
    this.strokeStartedAt = startedAt;
    this.ops.push(stroke);
    this.bytes += added;
    return true;
  }

  /**
   * Straighten the stroke in flight (Round 2 §2.5). The raw points stay exactly as
   * captured (guardrail #3); the snap only records where it happened. Points that
   * arrive afterwards aim the line's far end. Returns true if this call snapped it.
   */
  snapStroke(): boolean {
    const op = this.inFlight();
    if (!op || op.snap) return false;
    op.snap = { kind: 'line', at: op.points.length - 1 };
    // Fits inside the reserve every growing stroke keeps free.
    this.bytes += `,"snap":${JSON.stringify(op.snap)}`.length;
    return true;
  }

  /** The stroke being drawn right now is a straight line. */
  get isSnapped(): boolean {
    return !!this.inFlight()?.snap;
  }

  private inFlight(): StrokeOp | null {
    if (!this.drawing) return null;
    const op = this.ops[this.ops.length - 1];
    return op && op.kind === 'stroke' ? op : null;
  }

  /** Returns false when the point would not fit: the stroke simply stops growing. */
  extendStroke(x: number, y: number, pressure?: number): boolean {
    if (!this.drawing) return false;
    const op = this.ops[this.ops.length - 1];
    if (!op || op.kind !== 'stroke') return false;
    const point = this.point(x, y, pressure);
    const added = JSON.stringify(point).length + 1;
    const reserve = op.snap ? 0 : SNAP_RESERVE_BYTES;
    if (this.bytes + added + reserve > this.maxBytes) return false;
    op.points.push(point);
    this.bytes += added;
    return true;
  }

  endStroke(): void {
    this.drawing = false;
    this.measure();
  }

  /**
   * Throw away the stroke in flight. Used when a second finger lands: that first
   * touch was the start of a pinch, not a mark someone meant to make.
   */
  abortStroke(): void {
    if (!this.drawing) return;
    this.drawing = false;
    const op = this.ops[this.ops.length - 1];
    if (op && op.kind === 'stroke') this.ops.pop();
    this.measure();
  }

  private point(x: number, y: number, pressure?: number, at = this.now()): Point {
    return this.pointAt(x, y, pressure, Math.round(at - this.strokeStartedAt));
  }

  /**
   * A captured point at storage precision (guardrail #3 as amended): clamped into
   * the panel, x/y/pressure rounded to 4 decimals, t in whole milliseconds.
   * Normalized already (guardrail #4); the canvas converts before calling in.
   */
  private pointAt(x: number, y: number, pressure: number | undefined, t: number): Point {
    const nx = roundCoord(clamp01(x));
    const ny = roundCoord(clamp01(y));
    return pressure === undefined ? [nx, ny, t] : [nx, ny, t, roundCoord(clamp01(pressure))];
  }

  /* --------------------------------------------------------------- undo */

  /**
   * Tap undo: the whole last op disappears. Instant.
   *
   * A straightened stroke with a hand-drawn part (snapped mid-stroke) comes off
   * in two steps: first the snap (back to the wobble, points up to the snap),
   * then the stroke itself. A Shift line drawn straight from the start has no
   * hand-drawn part, so it goes in one step like any other op.
   */
  undo(): boolean {
    this.cancelPartialUndo();
    const index = this.ops.length - 1;
    const last = this.ops[index];
    if (last && hasHandDrawnPart(last)) {
      this.redo.push(this.unsnap(index));
      this.measure();
      return true;
    }
    const op = this.ops.pop();
    if (!op) return false;
    this.redo.push({ type: 'op', op });
    this.measure();
    return true;
  }

  /** Put a straightened stroke back to how it was drawn, and say how to redo it. */
  private unsnap(index: number): RedoEntry {
    const op = this.ops[index] as StrokeOp;
    const snap = op.snap!;
    const aiming = op.points.splice(snap.at + 1);
    delete op.snap;
    return { type: 'snap', opIndex: index, snap, points: aiming };
  }

  /* ------------------------------------------------- press-and-hold undo */

  /**
   * Begin rewinding the last stroke. Call stepPartialUndo() each animation frame
   * while the button is held, then endPartialUndo() on release.
   */
  beginPartialUndo(): boolean {
    if (this.pendingTail) return true;
    const index = this.ops.length - 1;
    const op = this.ops[index];
    if (!op || op.kind !== 'stroke') return false;

    this.redo = [];
    // A stroke snapped mid-way un-snaps first, then rewinds its hand-drawn
    // points; redo brings back the points, then the snap. A Shift line from the
    // start just rewinds, its far end walking back along the aiming path.
    if (hasHandDrawnPart(op)) this.redo.push(this.unsnap(index));
    this.pendingTail = [];
    this.pendingOpIndex = index;
    this.pendingOriginal = cloneOp(op);
    this.pendingRemovedOp = false;
    return true;
  }

  /**
   * Peel `count` points off the end of the stroke being rewound. Returns false
   * once there is nothing left to rewind — the rewind stops at the stroke it
   * started on rather than eating into earlier strokes.
   */
  stepPartialUndo(count = 1): boolean {
    if (!this.pendingTail || this.pendingRemovedOp) return false;
    const op = this.ops[this.pendingOpIndex];
    if (!op || op.kind !== 'stroke') return false;

    for (let i = 0; i < count; i++) {
      const point = op.points.pop();
      if (!point) break;
      this.pendingTail.unshift(point);
    }

    if (op.points.length === 0) {
      // Rewound to nothing: the op itself goes away.
      this.ops.splice(this.pendingOpIndex, 1);
      this.pendingRemovedOp = true;
      return false;
    }
    return true;
  }

  /** Release. Commits the truncation as one redoable step. */
  endPartialUndo(): void {
    if (!this.pendingTail) return;
    const tail = this.pendingTail;
    const removedOp = this.pendingRemovedOp;
    const original = this.pendingOriginal;
    const opIndex = this.pendingOpIndex;
    this.clearPartialState();

    this.measure();
    if (tail.length === 0) return;
    if (removedOp && original) {
      this.redo.push({ type: 'op', op: original });
    } else {
      this.redo.push({ type: 'tail', opIndex, points: tail });
    }
  }

  /** Abandon an in-flight rewind, keeping whatever has already been peeled off. */
  cancelPartialUndo(): void {
    if (this.pendingTail) this.endPartialUndo();
  }

  private clearPartialState(): void {
    this.pendingTail = null;
    this.pendingOpIndex = -1;
    this.pendingOriginal = null;
    this.pendingRemovedOp = false;
  }

  /** True while a hold-rewind is in flight (the canvas repaints every frame). */
  get isRewinding(): boolean {
    return this.pendingTail !== null;
  }

  /* --------------------------------------------------------------- redo */

  redoStep(): boolean {
    this.cancelPartialUndo();
    const entry = this.redo.pop();
    if (!entry) return false;

    // Redo only ever returns the log to a state it was in before, which was
    // within the limit: redo is cleared the moment anything new is drawn.
    if (entry.type === 'op') {
      this.ops.push(entry.op);
      this.measure();
      return true;
    }

    const op = this.ops[entry.opIndex];
    if (!op || op.kind !== 'stroke') return false;
    // Replay a partially-rewound stroke's truncated tail, or re-straighten a
    // stroke along with the aiming that followed its snap.
    op.points.push(...entry.points);
    if (entry.type === 'snap') op.snap = entry.snap;
    this.measure();
    return true;
  }

  /* -------------------------------------------------------- persistence */

  toOpLog(): OpLog {
    return { v: 1, ops: this.ops };
  }

  snapshot(): DrawingSnapshot {
    return { ops: this.ops, redo: this.redo };
  }

  /**
   * Restore a draft, including its undo history where we have it (guardrail #8).
   * Drafts saved before Round 2.2 hold full-precision points; they come back at
   * storage precision, with every point and timestamp kept.
   */
  restore(snapshot: DrawingSnapshot): void {
    this.ops = Array.isArray(snapshot.ops) ? snapshot.ops.map(roundOp) : [];
    this.redo = Array.isArray(snapshot.redo) ? snapshot.redo.map(roundEntry) : [];
    this.clearPartialState();
    this.drawing = false;
    this.measure();
  }

  get isEmpty(): boolean {
    return this.ops.length === 0;
  }
}

/* ------------------------------------------------------- rewind pacing */

/**
 * Press-and-hold rewind speed, in points per second. Lower is slower and easier
 * to stop on exactly the right spot — this is the number to turn if the rewind
 * feels wrong.
 */
export const REWIND_POINTS_PER_SECOND = 12;

/** However long the stroke, holding undo clears it within this many seconds. */
export const REWIND_MAX_SECONDS = 6;

/**
 * How fast to rewind a stroke of this length. A steady slow rate for ordinary
 * strokes, speeding up only enough that a very long scribble still finishes.
 */
export function rewindRate(strokeLength: number): number {
  return Math.max(REWIND_POINTS_PER_SECOND, strokeLength / REWIND_MAX_SECONDS);
}

/**
 * Total points that should be gone `elapsedMs` into a hold. Expressed against
 * elapsed time rather than frames rendered, so a 120Hz screen rewinds at the same
 * speed as a 60Hz one and a dropped frame does not slow the rewind down.
 */
export function pointsRewoundAfter(strokeLength: number, elapsedMs: number): number {
  if (elapsedMs <= 0) return 0;
  return Math.min(strokeLength, Math.floor((elapsedMs / 1000) * rewindRate(strokeLength)));
}

/* ------------------------------------------------------- straight lines */

/**
 * Phones straighten a stroke when the finger holds still at the end of it: for
 * SNAP_STILL_MS, within SNAP_STILL_RADIUS_PX of where it stopped. Strokes shorter
 * than SNAP_MIN_LENGTH_PX (a dot, a tiny tick) never snap. Desktop uses Shift.
 */
export const SNAP_STILL_MS = 500;
export const SNAP_STILL_RADIUS_PX = 8;
export const SNAP_MIN_LENGTH_PX = 24;

/**
 * Which pointers hold-to-snap. Not the mouse: someone pausing mid-stroke with a
 * mouse is thinking, and Shift is right there.
 */
export const SNAP_HOLD_POINTER_TYPES: readonly string[] = ['touch', 'pen'];

/** Straightened after some hand-drawing (snap.at > 0), so undo has a wobble to go back to. */
function hasHandDrawnPart(op: Op): boolean {
  return op.kind === 'stroke' && !!op.snap && op.snap.at > 0;
}

function clamp01(n: number): number {
  return n < 0 ? 0 : n > 1 ? 1 : n;
}

function roundOp(op: Op): Op {
  return op.kind === 'stroke' ? { ...op, points: op.points.map(roundPoint) } : op;
}

function roundEntry(entry: RedoEntry): RedoEntry {
  return entry.type === 'op' ? { ...entry, op: roundOp(entry.op) } : { ...entry, points: entry.points.map(roundPoint) };
}

function cloneOp(op: Op): Op {
  return { ...op, points: op.points.map((p) => [...p] as Point) };
}
